import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { reviews } from "@/mocks/reviews";

const reportReviews = reviews.filter((r) =>
  r.content.includes("리포트") || r.content.includes("물건분석") || r.content.includes("체크리스트")
);

// fallback: at least show some reviews even if no keyword match
const displayReviews = reportReviews.length >= 3 ? reportReviews : reviews.slice(0, 3);

export default function SuccessSlider() {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const goTo = useCallback((idx: number) => {
    setCurrent(idx);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(() => {
      setCurrent((prev) => (prev + 1) % displayReviews.length);
    }, 4500);
    return () => clearInterval(id);
  }, [isPaused]);

  return (
    <div
      className="bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
            <i className="ri-trophy-line text-amber-500 text-sm"></i>
          </div>
          <h3 className="text-sm font-bold text-[#1C1917]">고객 성공 사례</h3>
        </div>
        <div className="flex items-center gap-1">
          {displayReviews.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                i === current ? "bg-[#C4956A] w-4" : "bg-[#E8E5E0] hover:bg-[#D4CFC8]"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Slider */}
      <div className="relative overflow-hidden" style={{ minHeight: "140px" }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.35, ease: "easeOut" as const }}
            className="absolute inset-0"
          >
            {/* Stars */}
            <div className="flex items-center gap-0.5 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <i
                  key={star}
                  className={`text-xs ${star <= displayReviews[current].rating ? "ri-star-fill text-amber-400" : "ri-star-line text-[#D4CFC8]"}`}
                ></i>
              ))}
            </div>

            {/* Quote */}
            <p className="text-sm text-[#5C5650] leading-relaxed mb-3 line-clamp-3">
              &ldquo;{displayReviews[current].content}&rdquo;
            </p>

            {/* Author */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border border-[#E8E5E0]">
                <img loading="lazy" decoding="async"
                  src={displayReviews[current].avatar}
                  alt={displayReviews[current].name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-[#1C1917]">{displayReviews[current].name}</p>
                <p className="text-[10px] text-[#8C8780]">{displayReviews[current].role}</p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}